/* oxlint-disable unicorn/prefer-add-event-listener -- SocketLike is the WebSocket on* subset @w/realtime defines (memory-host.ts); this file implements it */
import { createRealtimeClient, type RealtimeClient, type LiveEdit, type SocketLike } from '@w/realtime'
import { retry } from '@w/utils/retry'
import { journalLiveFrame, liveJournalFrames } from '../db/live-outbox-journal'
import type { RealtimeConnection } from '../server/realtime/memory-presence'

/** The server's presence endpoint (presence-route.ts PRESENCE_PATH). */
const PRESENCE_PATH = '/api/presence'

export interface WorkspaceRealtimeClient extends RealtimeClient {
  publishLive(edit: LiveEdit): boolean
  settleLive(pageId: string, epoch?: number): Promise<void>
}

class LiveCoveragePending extends Error {
  constructor() { super('Live edits await durable page coverage') }
}

/**
 * One realtime client per open workspace, only for platform admins: the server gate refuses everyone else
 * (presence-access.ts, `user.role !== 'admin'` → 403), so a non-admin opens no socket at all
 * (page-collaboration.spec.md, Access). A peer's poke and every reconnect run one pull of that workspace; a refused
 * connect (1008) stops the client (realtime.spec.md, Client).
 */
export function createRealtimeClients(options: {
  user: () => { id: string; role?: string | null } | null
  connect: (workspaceId: string) => SocketLike
  pull: (workspaceId: string) => Promise<unknown>
  storage?: Storage
  pending?: (workspace: string, pageId: string, userId: string, epoch?: number) => Promise<{ bytes: Uint8Array; outgoing: boolean }[]>
  stage: (workspaceId: string, frame: { pageId: string; userId: string; bytes: Uint8Array; direction: 'incoming' | 'outgoing' }) => Promise<void>
}) {
  const clients = new Map<string, { userId: string; client: WorkspaceRealtimeClient }>()
  const pull = async (workspaceId: string) => { await options.pull(workspaceId) }
  const close = (workspaceId: string) => {
    clients.get(workspaceId)?.client.close()
    clients.delete(workspaceId)
  }
  return {
    /** Opens the workspace's client for a signed-in admin, beside the workspace's file (spaces-resources). */
    open: (workspaceId: string) => {
      const user = options.user()
      if (clients.get(workspaceId)?.userId === user?.id) return
      close(workspaceId)
      if (!user || user.role !== 'admin') return
      let stages = Promise.resolve()
      const stage = (pageId: string, update: string) => options.stage(workspaceId, { pageId, userId: user.id, bytes: Uint8Array.fromBase64(update), direction: 'outgoing' })
      let welcomed = false
      const transport = createRealtimeClient({
        connect: () => options.connect(workspaceId),
        onEdit: edit => {
          // On the same tail as outgoing stages, so settling sees every frame received before it began.
          const received = () => options.stage(workspaceId, { pageId: edit.pageId, userId: edit.userId, bytes: Uint8Array.fromBase64(edit.update), direction: 'incoming' })
          // No journal holds a received frame: report its failed stage at the root boundary (global-errors.ts), keep the tail usable.
          stages = stages.then(received, received).catch((error: unknown) => { reportError(error) })
        },
        onPoke: () => pull(workspaceId),
        onReconnect: () => {
          if (welcomed) void pull(workspaceId).catch(() => {})
          welcomed = true
        },
      })
      const client: WorkspaceRealtimeClient = Object.assign(transport, {
        publishLive(edit: LiveEdit) {
          if (!options.storage || !options.pending || !transport.supportsEdits()) return false
          const key = journalLiveFrame(options.storage, user.id, workspaceId, edit.pageId, edit.update)
          const send = async () => {
            await stage(edit.pageId, edit.update)
            options.storage?.removeItem(key)
            transport.sendEdit(edit)
          }
          stages = stages.then(send, send)
          return true
        },
        async settleLive(pageId: string, epoch?: number) {
          if (!options.storage || !options.pending) return
          // A failed stage keeps its journal entry; the replay below stages it again and reports a lasting failure.
          await stages.catch(() => {})
          const pending = options.pending
          const storage = options.storage
          await retry(async () => {
            for (const frame of liveJournalFrames(storage, user.id, workspaceId)) {
              await stage(frame.pageId, frame.update)
              storage.removeItem(frame.key)
            }
            const frames = await pending(workspaceId, pageId, user.id, epoch)
            if (frames.length === 0) return
            if (transport.supportsEdits()) for (const frame of frames) if (frame.outgoing) transport.sendEdit({ pageId, update: frame.bytes.toBase64() })
            await pull(workspaceId)
            if ((await pending(workspaceId, pageId, user.id, epoch)).length > 0) throw new LiveCoveragePending()
          }, { retries: 30, minTimeout: 100, maxTimeout: 1000, factor: 1.2, shouldRetry: ({ error }) => error instanceof LiveCoveragePending })
        },
      })
      clients.set(workspaceId, { userId: user.id, client })
    },
    close,
    /** The open client of the workspace, or null; a pure read, safe in render. */
    get(workspaceId: string): WorkspaceRealtimeClient | null {
      return clients.get(workspaceId)?.client ?? null
    },
    /** Tells the workspace's peers that a round pushed rows, so they pull (the client sends nothing while offline). */
    poke: (workspaceId: string) => {
      clients.get(workspaceId)?.client.poke()
    },
    closeAll() {
      for (const { client } of clients.values()) client.close()
      clients.clear()
    },
  }
}

/** The browser WebSocket URL of a workspace's presence room, on the backend's origin. */
export function presenceUrl(backendUrl: string, workspaceId: string): string {
  const backend = new URL(backendUrl || '/', globalThis.location.origin)
  const url = new URL(`${PRESENCE_PATH}?workspace=${encodeURIComponent(workspaceId)}`, backend)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return url.toString()
}

/** A browser WebSocket as the client's socket; the client sends only after the room's welcome, so never before open. */
export function browserSocket(url: string): SocketLike {
  const ws = new WebSocket(url)
  const socket: SocketLike = {
    send(data) { ws.send(data) },
    close(code, reason) { ws.close(code, reason) },
    onopen: null,
    onmessage: null,
    onclose: null,
  }
  ws.addEventListener('open', event => { socket.onopen?.(event) })
  ws.addEventListener('message', event => { socket.onmessage?.({ data: event.data }) })
  ws.addEventListener('close', event => { socket.onclose?.({ code: event.code, reason: event.reason }) })
  return socket
}

/**
 * A socket over the in-memory server's async gate (memory-presence.ts): frames sent before the gate answers are
 * queued; a refused connect (401/403) closes with 1008, as a refused WebSocket upgrade does.
 */
export function socketFromConnection(connection: Promise<RealtimeConnection>): SocketLike {
  let inner: SocketLike | null = null
  let closed = false
  const queued: string[] = []
  const socket: SocketLike = {
    send(data) {
      if (inner) inner.send(data)
      else queued.push(data)
    },
    close(code, reason) {
      if (closed) return
      closed = true
      if (inner) inner.close(code, reason)
    },
    onopen: null,
    onmessage: null,
    onclose: null,
  }
  const attach = (answer: RealtimeConnection) => {
    if (answer.status !== 200) {
      // 403 stops the client (1008); an expired sign-in (401) reconnects after the app renewed it over HTTP.
      if (!closed) { closed = true; socket.onclose?.({ code: answer.status === 403 ? 1008 : 1006, reason: String(answer.status) }) }
      return
    }
    if (closed) { answer.socket.close(1000, 'closed'); return }
    const opened = answer.socket
    inner = opened
    opened.onopen = event => { socket.onopen?.(event) }
    opened.onmessage = event => { socket.onmessage?.(event) }
    opened.onclose = event => { socket.onclose?.(event) }
    for (const data of queued.splice(0)) opened.send(data)
  }
  void connection.then(attach, () => {
    if (!closed) { closed = true; socket.onclose?.({ code: 1006, reason: 'connect failed' }) }
  })
  return socket
}
